import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { EyeOutlined, EyeInvisibleOutlined, ExclamationCircleFilled } from "@ant-design/icons";
import Cookies from "js-cookie";

import { SetPasswordDetails } from "../../api/types/auth/authTypes";
import { useNavigate } from "react-router-dom";
import authService from "../../api/auth/authService";
import AuthShell from "./AuthShell";

const SetPassword: React.FC = () => {
  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const data: any = params.get("data");
  const [loading, setLoading] = useState<boolean>(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [errorVal, setError] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    getValues,
  } = useForm<SetPasswordDetails>();

  const handleFormSubmit = async (formdata: any) => {
    setError("");
    setLoading(true);
    try {
      let parsedData = JSON.parse(params.get("data") || "");
      console.log("parsed", parsedData);
      let formData = {
        id: parsedData.userId,
        password: formdata.password,
      };
      Cookies.set("access-token", JSON.stringify(parsedData.token), {
        expires: 7,
        secure: window.location.protocol === "https:",
        sameSite: "Strict",
      });
      const resData = await authService.setPassword(
        formData,
        JSON.stringify(parsedData.token)
      );
      console.log("resData", resData);
      if (resData.status) {
        setTimeout(() => {
          navigate("/success", {
            state: {
              title: "Your password has been successfully set.",
              description:
                "You can now continue using the ProcureX platform, please login",
            },
          });
        }, 1000);
        setLoading(false);
      } else {
        setError(data.message);
      }
    } catch (err: any) {
      console.log("Login failed:", err.message);
      setError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log("data111", data);
  }, []);

  return (
    <AuthShell
      title="Set Password"
      heading="Welcome"
      subheading="Set your password to access Procurex."
      brandHeadline="Built for the people at the face."
      brandSubcopy="One login gets you the schedule, the norms, the daily progress report and the cost behind every task."
    >
      {errorVal && (
        <div className="pxauth__alert" role="alert">
          <ExclamationCircleFilled className="pxauth__alertIcon" aria-hidden="true" />
          <span className="pxauth__alertText">{errorVal}</span>
        </div>
      )}

      <form
        className="pxauth-form"
        noValidate
        onSubmit={handleSubmit(handleFormSubmit)}
      >
        <div className="pxauth-field">
          <div className="pxauth-labelRow">
            <label className="pxauth-label" htmlFor="pxauth-password">
              Password
            </label>
          </div>
          <div className="pxauth-control pxauth-control--password">
            <input
              id="pxauth-password"
              type={passwordVisible ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Enter your password"
              className={`pxauth-input${errors.password ? " is-invalid" : ""}`}
              aria-invalid={!!errors.password}
              aria-describedby={
                errors.password ? "pxauth-password-error" : "pxauth-password-hint"
              }
              {...register("password", {
                required: "This field is required",
                pattern: {
                  value: /^(?=.*[A-Z])(?=.*[@$#^_-])[A-Za-z\d@$#^_-]{8,}$/,
                  message:
                    "Password must contain at least 8 characters, one uppercase, one lowercase, one number, and one special character. Only the following special characters are allowed: @$!%*?&#^_-",
                },
              })}
            />
            <button
              type="button"
              className="pxauth-reveal"
              aria-label={passwordVisible ? "Hide password" : "Show password"}
              aria-pressed={passwordVisible}
              aria-controls="pxauth-password"
              onClick={() => setPasswordVisible((prev) => !prev)}
            >
              {passwordVisible ? (
                <EyeInvisibleOutlined aria-hidden="true" />
              ) : (
                <EyeOutlined aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.password ? (
            <p className="pxauth-error" id="pxauth-password-error" role="alert">
              {errors.password.message}
            </p>
          ) : (
            <p className="pxauth-hint" id="pxauth-password-hint">
              At least 8 characters with an uppercase letter, a number and one
              of @$!%*?&amp;#^_-
            </p>
          )}
        </div>

        <div className="pxauth-field">
          <div className="pxauth-labelRow">
            <label className="pxauth-label" htmlFor="pxauth-confirmpassword">
              Confirm password
            </label>
          </div>
          <div className="pxauth-control pxauth-control--password">
            <input
              id="pxauth-confirmpassword"
              type={confirmPasswordVisible ? "text" : "password"}
              autoComplete="new-password"
              placeholder="Confirm your password"
              className={`pxauth-input${
                errors.confirmpassword ? " is-invalid" : ""
              }`}
              aria-invalid={!!errors.confirmpassword}
              aria-describedby={
                errors.confirmpassword ? "pxauth-confirm-error" : undefined
              }
              {...register("confirmpassword", {
                required: "This field is required",
                validate: (value) =>
                  value === getValues("password") || "Passwords must match",
              })}
            />
            <button
              type="button"
              className="pxauth-reveal"
              aria-label={
                confirmPasswordVisible ? "Hide password" : "Show password"
              }
              aria-pressed={confirmPasswordVisible}
              aria-controls="pxauth-confirmpassword"
              onClick={() => setConfirmPasswordVisible((prev) => !prev)}
            >
              {confirmPasswordVisible ? (
                <EyeInvisibleOutlined aria-hidden="true" />
              ) : (
                <EyeOutlined aria-hidden="true" />
              )}
            </button>
          </div>
          {errors.confirmpassword && (
            <p className="pxauth-error" id="pxauth-confirm-error" role="alert">
              {errors.confirmpassword.message}
            </p>
          )}
        </div>

        <button type="submit" className="pxauth-btn" disabled={loading}>
          {loading ? (
            <>
              <span className="pxauth-spinner" aria-hidden="true" />
              Saving&hellip;
            </>
          ) : (
            "Submit"
          )}
        </button>
      </form>
    </AuthShell>
  );
};

export default SetPassword;
